import React from 'react';
import { Box, Button, Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasSystemsCard,
  EntityHasResourcesCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import {
  EntityTechdocsContent,
  isTechDocsAvailable,
} from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';

import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';

import {
  Sev2BoardPage,
  EngineerPerformanceComponent,
  isSev2BoardAvailable,
  isTribe,
  AthSePage,
} from '@internal/backstage-plugin-sev2';
import {
  UptimeMonitorPage,
  isHealthCheckAvailable,
  IssuesWrapperComponent,
  isErrorsCheckAvailable,
  isPerformanceAvailable,
  PerformanceWrapperComponent,
} from '@internal/backstage-plugin-uptime-monitor';
import { isInfisicalProjectAvailable } from '@internal/backstage-plugin-infisical';
import { InfisicalSecretsPage } from '@internal/backstage-plugin-infisical';
import { isTrackingIdAvailable } from '@internal/backstage-plugin-umami-analytics';

import {
  EntityAdrContent,
  isAdrAvailable,
} from '@backstage-community/plugin-adr';
import { FigmaPage, isFigmaAvailable } from '@internal/backstage-plugin-figma';
import QualityMetricLineChart from '../metric/QualityMetricLineChart';
import QualityMetricList from '../metric/QualityMetricList';
import { CustomEntityHasSubcomponentsCard } from './CustomEntityHasSubcomponentsCard';
import { SearchTraceListPage } from '@internal/backstage-plugin-jaeger-custom';
import { isJaegerAvailable } from '@backstage-community/plugin-jaeger';
import { UserAccessWrapper } from '../utils/UserAccessWrapper';
import {
  PrometheusPage,
  isMetricsAvailable,
} from '@internal/backstage-plugin-prometheus';
import { GatusMonitorPage } from '@internal/backstage-plugin-gatus-monitor';
import {
  SystemMaturityPage,
  ComponentMaturityPage,
  ComponentMaturitySummaryCard,
  SystemMaturitySummaryCard,
} from '@internal/backstage-plugin-maturity';
import { UmamiAnalyticsPage } from '@internal/backstage-plugin-umami-analytics';
import { EntitySimpleManifestEditorContent } from '@internal/backstage-plugin-simple-manifest-editor';
import { DocumentationAlerts } from './DocumentationAlerts';
import { CustomAboutCard } from './CustomAboutCard';
import { UserStoriesContent } from '@internal/backstage-plugin-user-story';
import { EntitySystemHandoverContent } from '@internal/backstage-plugin-handover';

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const cicdContent = (
  // This is an example of how you can implement your company's logic in entity page.
  // You can for example enforce that all components of type 'service' should use GitHubActions
  <EntitySwitch>
    {/*
      Here you can add support for different CI/CD services, for example
      using @backstage-community/plugin-github-actions as follows:
      <EntitySwitch.Case if={isGithubActionsAvailable}>
        <EntityGithubActionsContent />
      </EntitySwitch.Case>
     */}

    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <DocumentationAlerts />
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6} xs={12}>
      <Grid container spacing={3} direction="column" wrap="nowrap">
        <Grid item xs={12}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={isKind('component')}>
            <Grid item xs={12}>
              <ComponentMaturitySummaryCard />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard
        variant="gridItem"
        height={400}
      />
    </Grid>
    {/* <UserAccessWrapper exposeToUser={'internal'}>
      <Grid item md={12} xs={12}>
        <RejectionSummaryComponent />
      </Grid>
    </UserAccessWrapper> */}
    <Grid item md={4} xs={12}>
      <EntityLinksCard />
    </Grid>
    <Grid item xs={12}>
      <CustomEntityHasSubcomponentsCard variant="gridItem" />
    </Grid>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/maturity" title="Maturity Level">
      <ComponentMaturityPage />
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <EntityProvidedApisCard tableOptions={{ search: true }} />
        </Grid>
        <Grid item xs={12}>
          <EntityConsumedApisCard tableOptions={{ search: true }} />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <EntityDependsOnComponentsCard
            variant="gridItem"
            tableOptions={{ search: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <EntityDependsOnResourcesCard
            variant="gridItem"
            tableOptions={{ search: true }}
          />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route if={isTechDocsAvailable} path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADR">
      <EntityAdrContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/issues" title="Issues" if={isSev2BoardAvailable}>
      <Sev2BoardPage />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/secrets"
      title="Secrets"
      if={isInfisicalProjectAvailable}
    >
      <InfisicalSecretsPage />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/uptime"
      title="Uptime"
      if={isHealthCheckAvailable}
    >
      <UptimeMonitorPage />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/errors"
      title="Error Tracking"
      if={isErrorsCheckAvailable}
    >
      <IssuesWrapperComponent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/apm"
      title="Performance"
      if={isPerformanceAvailable}
    >
      <PerformanceWrapperComponent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/tracers" title="Tracers" if={isJaegerAvailable}>
      <SearchTraceListPage />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/resource"
      title="Resource"
      if={isMetricsAvailable}
    >
      <PrometheusPage />
    </EntityLayout.Route>

    <EntityLayout.Route path="/manifest" title="Edit">
      <EntitySimpleManifestEditorContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <EntityDependsOnComponentsCard
            variant="gridItem"
            tableOptions={{ search: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <EntityDependsOnResourcesCard
            variant="gridItem"
            tableOptions={{ search: true }}
          />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route if={isTechDocsAvailable} path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADR">
      <EntityAdrContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/issues" title="Issues" if={isSev2BoardAvailable}>
      <Sev2BoardPage />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/secrets"
      title="Secrets"
      if={isInfisicalProjectAvailable}
    >
      <InfisicalSecretsPage />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/uptime"
      title="Uptime"
      if={isHealthCheckAvailable}
    >
      <UptimeMonitorPage />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/errors"
      title="Error Tracking"
      if={isErrorsCheckAvailable}
    >
      <IssuesWrapperComponent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/apm"
      title="Performance"
      if={isPerformanceAvailable}
    >
      <PerformanceWrapperComponent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/tracers" title="Tracers" if={isJaegerAvailable}>
      <SearchTraceListPage />
    </EntityLayout.Route>

    <EntityLayout.Route path="/manifest" title="Edit">
      <EntitySimpleManifestEditorContent />
    </EntityLayout.Route>
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/maturity" title="Maturity Level">
      <ComponentMaturityPage />
    </EntityLayout.Route>

    <EntityLayout.Route if={isTechDocsAvailable} path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADR">
      <EntityAdrContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/issues" title="Issues" if={isSev2BoardAvailable}>
      <Sev2BoardPage />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/secrets"
      title="Secrets"
      if={isInfisicalProjectAvailable}
    >
      <InfisicalSecretsPage />
    </EntityLayout.Route>

    <EntityLayout.Route path="/figma" title="Figma" if={isFigmaAvailable}>
      <FigmaPage />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/metrics"
      title="Resource"
      if={isMetricsAvailable}
    >
      <PrometheusPage />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/uptime"
      title="Uptime"
      if={isHealthCheckAvailable}
    >
      <UptimeMonitorPage />
    </EntityLayout.Route>

    <EntityLayout.Route path="/tracers" title="Tracers" if={isJaegerAvailable}>
      <SearchTraceListPage />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/errors"
      title="Error Tracking"
      if={isErrorsCheckAvailable}
    >
      <IssuesWrapperComponent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/apm"
      title="Performance"
      if={isPerformanceAvailable}
    >
      <PerformanceWrapperComponent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/manifest" title="Edit">
      <EntitySimpleManifestEditorContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const screenPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard
            variant="gridItem"
            height={400}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/manifest" title="Edit">
      <EntitySimpleManifestEditorContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard
            variant="gridItem"
            entityFilterKind={['System', 'Component', 'API', 'Screen', 'Story']}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <UserAccessWrapper exposeToUser="internal">
            <QualityMetricLineChart isUser />
            <QualityMetricList isUser />
          </UserAccessWrapper>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/performance" title="Performance">
      <EngineerPerformanceComponent />
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard
            variant="gridItem"
            entityFilterKind={['System', 'Component', 'API', 'Screen', 'Story']}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <UserAccessWrapper exposeToUser="internal">
            <AthSePage />
            <Box m={2} />
            <QualityMetricLineChart />
            <QualityMetricList />
          </UserAccessWrapper>
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/issues" title="Issues" if={isTribe}>
      <Sev2BoardPage />
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6} xs={12}>
          <Grid container spacing={3} direction="column" wrap="nowrap">
            <Grid item xs={12}>
              <CustomAboutCard variant="gridItem" />
            </Grid>
            <Grid item xs={12}>
              <SystemMaturitySummaryCard />
            </Grid>
          </Grid>
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item xs={12}>
          <EntityHasComponentsCard
            variant="gridItem"
            tableOptions={{ search: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <EntityHasApisCard
            variant="gridItem"
            tableOptions={{ search: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <EntityHasResourcesCard
            variant="gridItem"
            tableOptions={{ search: true }}
          />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
    <EntityLayout.Route path="/stories" title="User Stories">
      <UserStoriesContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/handover" title="Handover">
      <EntitySystemHandoverContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/maturity" title="Maturity Level">
      <SystemMaturityPage />
    </EntityLayout.Route>
    <EntityLayout.Route path="/uptime" title="Uptime">
      <GatusMonitorPage />
    </EntityLayout.Route>
    <EntityLayout.Route if={isTechDocsAvailable} path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
    <EntityLayout.Route if={isAdrAvailable} path="/adrs" title="ADR">
      <EntityAdrContent />
    </EntityLayout.Route>
    <EntityLayout.Route
      if={isTrackingIdAvailable}
      path="/analytics"
      title="Analytics"
    >
      <UmamiAnalyticsPage />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard
            variant="gridItem"
            height={400}
          />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case if={isKind('screen')} children={screenPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
